import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, FileText, Download, ChevronDown, ChevronRight, Search, ArrowLeft, Lock, Clock, CheckCircle2, Trash2, RefreshCw, FilePlus2, PenLine } from "lucide-react";
import PrepareAgreement from "./components/PrepareAgreement";
import ProviderSignatureManager from "./components/ProviderSignatureManager";
import DraftCleanupPanel from "./components/DraftCleanupPanel";

import { API, SENDING_ON } from "./lib/portalMode";
import PreviewNote from "./components/PreviewNote";
import { retentionNote, draftRowNote } from "./lib/draftRetention";
import { regenWarning, regenConfirmText, regenAllConfirmText, hasNoPdf } from "./lib/regenGuard";
import { pageParams, clientParams, readPage, appendPage, showingLine, EMPTY_TOTALS } from "./lib/adminList";

function AdminDashboard({ onBack }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [loginError, setLoginError] = useState("");
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedClients, setExpandedClients] = useState({});
  const [expandedAssessments, setExpandedAssessments] = useState({});
  const [statusFilter, setStatusFilter] = useState("all"); // all, completed, draft
  const [showPrepare, setShowPrepare] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showCleanup, setShowCleanup] = useState(false); // read-only draft clean-up counts
  const [retention, setRetention] = useState(null); // how long server drafts are kept (Round 2 PR D)
  // One page at a time, newest first; the totals count every record (lib/adminList.js)
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [matching, setMatching] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(""); // the search sent to the server, a moment after typing stops
  const [loadedOnce, setLoadedOnce] = useState(false); // the full-page spinner shows only before the first load

  // Helper to create auth headers
  const authHeaders = (token) => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // Check for existing session
  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isAuthenticated && authToken) {
      loadAssessments();
    }
  }, [isAuthenticated, authToken, statusFilter, search]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/forms/runtime-config`)
      .then((r) => setRetention((r.data && r.data.draft_retention) || null))
      .catch(() => setRetention(null));
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!SENDING_ON) return; // off the live portal nothing is sent (lib/portalMode.js)
    setLoginError("");
    try {
      // Exchange credentials for a signed, expiring session token (no password stored).
      const loginRes = await axios.post(`${API}/portal/login`, { username, password });
      const token = loginRes.data.token;
      // Checks the session works before showing the page; the list then loads newest first.
      await axios.get(`${API}/assessments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...pageParams(), limit: 1 }
      });
      setAuthToken(token);
      setIsAuthenticated(true);
      sessionStorage.setItem('adminToken', token);
    } catch (error) {
      if (error.response?.status === 401) {
        setLoginError("Invalid credentials");
      } else {
        setLoginError("Unable to connect to server. Please try again.");
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken("");
    sessionStorage.removeItem('adminToken');
    onBack();
  };

  const showPage = (data, append) => {
    const page = readPage(data);
    setAssessments((shown) => (append ? appendPage(shown, page.items) : page.items));
    setTotals(page.totals);
    setMatching(page.matching);
    setHasMore(page.hasMore);
  };

  // Every record of one client, wherever it sits in the list (Delete all, Regenerate all).
  const loadClient = async (clientName) => {
    const response = await axios.get(`${API}/assessments`, {
      ...authHeaders(authToken), params: clientParams(clientName)
    });
    return readPage(response.data).items;
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const response = await axios.get(`${API}/assessments`, {
        ...authHeaders(authToken),
        params: pageParams({ offset: assessments.length, status: statusFilter, search })
      });
      showPage(response.data, true);
    } catch (error) {
      console.error("Error loading more assessments:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/assessments`, {
        ...authHeaders(authToken),
        params: pageParams({ status: statusFilter, search })
      });
      showPage(response.data, false);
      setLoadedOnce(true);
    } catch (error) {
      console.error("Error loading assessments:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-slate-600" />
            </div>
            <CardTitle className="text-xl">Admin Login</CardTitle>
            <p className="text-sm text-slate-500 mt-1">Enter your credentials to access the admin dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  data-testid="admin-username-input"
                  autoComplete="username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  data-testid="admin-password-input"
                  autoComplete="current-password"
                />
              </div>
              {loginError && (
                <p className="text-sm text-red-500 text-center" data-testid="login-error">{loginError}</p>
              )}
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onBack}
                  className="flex-1"
                  data-testid="login-back-btn"
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={!SENDING_ON}
                  data-testid="login-submit-btn"
                >
                  Login
                </Button>
              </div>
              <PreviewNote inline />
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter assessments by status first
  const filteredByStatus = assessments.filter(assessment => {
    if (statusFilter === "all") return true;
    if (statusFilter === "completed") return assessment.status !== "draft";
    if (statusFilter === "draft") return assessment.status === "draft";
    if (statusFilter === "nopdf") return hasNoPdf(assessment);
    return true;
  });

  // Group assessments by client name
  const groupedAssessments = filteredByStatus.reduce((acc, assessment) => {
    const name = assessment.name || "Unknown";
    if (!acc[name]) {
      acc[name] = [];
    }
    acc[name].push(assessment);
    return acc;
  }, {});

  // Sort assessments within each group by date (newest first), with drafts at top
  Object.keys(groupedAssessments).forEach(name => {
    groupedAssessments[name].sort((a, b) => {
      // Drafts come first
      if (a.status === 'draft' && b.status !== 'draft') return -1;
      if (b.status === 'draft' && a.status !== 'draft') return 1;
      
      const dateA = new Date(a.timestamp || a.last_saved || a.date);
      const dateB = new Date(b.timestamp || b.last_saved || b.date);
      return dateB - dateA;
    });
  });

  // Filter clients by search term
  const filteredClients = Object.keys(groupedAssessments)
    .filter(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));

  // Totals for every record, not just the page shown (from the server)
  const totalDrafts = totals.drafts;
  const totalCompleted = totals.completed;
  const totalNoPdf = totals.no_pdf;

  const toggleClient = (name) => {
    setExpandedClients(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const toggleAssessment = (id) => {
    setExpandedAssessments(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const downloadPdf = async (assessmentId) => {
    try {
      const response = await axios.get(`${API}/assessments/pdf/${assessmentId}`, {
        responseType: 'blob',
        ...authHeaders(authToken)
      });
      
      // Check if we got a valid PDF
      if (response.data.size === 0) {
        alert("PDF not found or empty");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assessment_${assessmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      if (error.response?.status === 404) {
        alert("PDF not found. The PDF may not have been generated for this assessment. Try clicking 'Regenerate PDF' first.");
      } else {
        alert("Error downloading PDF. Please try again later.");
      }
    }
  };

  const regeneratePdf = async (assessmentId, assessmentType) => {
    try {
      const confirmMsg = regenConfirmText(assessmentType);
      if (!window.confirm(confirmMsg)) {
        return;
      }
      
      alert("Regenerating PDF... Please wait.");
      
      const response = await axios.post(`${API}/assessments/regenerate-pdf/${assessmentId}`, {}, authHeaders(authToken));
      
      if (response.data.success) {
        alert(`PDF regenerated successfully!\n\nYou can now download the PDF.`);
        // Refresh the assessment list
        loadAssessments();
      } else {
        alert("Failed to regenerate PDF: " + (response.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error regenerating PDF:", error);
      alert("Error regenerating PDF: " + (error.response?.data?.detail || error.message || "Please try again later."));
    }
  };

  const regenerateAllPdfsForClient = async (clientName) => {
    try {
      const confirmMsg = regenAllConfirmText(clientName, await loadClient(clientName));
      if (!window.confirm(confirmMsg)) {
        return;
      }
      
      alert("Regenerating all PDFs... Please wait. This may take up to a minute.");
      
      const response = await axios.post(`${API}/assessments/regenerate-pdfs-for-client?client_name=${encodeURIComponent(clientName)}`, {}, authHeaders(authToken));
      
      if (response.data.successful > 0) {
        alert(`PDFs regenerated!\n\nSuccessful: ${response.data.successful}\nFailed: ${response.data.failed}\n\nYou can now download the PDFs.`);
        // Refresh the assessment list
        loadAssessments();
      } else {
        alert("No PDFs were regenerated. " + (response.data.errors?.[0]?.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error regenerating PDFs:", error);
      alert("Error regenerating PDFs: " + (error.response?.data?.detail || error.message || "Please try again later."));
    }
  };

  const downloadDraftPdf = async (assessmentId) => {
    try {
      const response = await axios.get(`${API}/assessments/draft-pdf/${assessmentId}`, {
        responseType: 'blob',
        ...authHeaders(authToken)
      });
      
      // Check if we got a valid PDF
      if (response.data.size === 0) {
        alert("Failed to generate draft PDF");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `draft_${assessmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading draft PDF:", error);
      alert("Error generating draft PDF. Please try again later.");
    }
  };

  const deleteDraft = async (draftId) => {
    if (!window.confirm("Are you sure you want to delete this draft? This cannot be undone.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/assessments/draft/${draftId}`, authHeaders(authToken));
      // Reload assessments after deletion
      loadAssessments();
    } catch (error) {
      console.error("Error deleting draft:", error);
      alert("Error deleting draft. Please try again.");
    }
  };

  const deleteAssessment = async (assessmentId, clientName) => {
    const confirmMessage = `⚠️ WARNING: PERMANENT DELETION\n\nYou are about to permanently delete this assessment for "${clientName}".\n\nThis action CANNOT be undone. The assessment data and any associated PDF will be permanently removed.\n\nAre you sure you want to proceed?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Double confirmation for extra safety
    const doubleConfirm = window.confirm("This is your final confirmation.\n\nClick OK to permanently delete this assessment.");
    if (!doubleConfirm) {
      return;
    }
    
    try {
      await axios.delete(`${API}/assessments/${assessmentId}`, authHeaders(authToken));
      // Reload assessments after deletion
      loadAssessments();
      alert("Assessment deleted successfully.");
    } catch (error) {
      console.error("Error deleting assessment:", error);
      alert("Error deleting assessment. Please try again.");
    }
  };

  const deleteAllClientAssessments = async (clientName) => {
    let assessmentsList;
    try {
      assessmentsList = await loadClient(clientName); // all of them, not just the ones loaded on this page
    } catch (error) {
      console.error("Error loading the client's assessments:", error);
      alert("Couldn't load this client's assessments. Please try again.");
      return;
    }
    const count = assessmentsList.length;
    const confirmMessage = `⚠️ WARNING: DELETE ALL CLIENT DATA\n\nYou are about to permanently delete ALL ${count} assessment(s) for "${clientName}".\n\nThis will remove:\n• All completed assessments\n• All in-progress drafts\n• All associated PDF reports\n\nThis action CANNOT be undone.\n\nAre you sure you want to proceed?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // Double confirmation
    const doubleConfirm = window.confirm(`FINAL CONFIRMATION\n\nYou are about to delete ALL data for "${clientName}".\n\nThis client will be permanently removed from the system.\n\nClick OK to confirm deletion.`);
    if (!doubleConfirm) {
      return;
    }
    
    try {
      // Delete all assessments for this client
      let deletedCount = 0;
      let failedCount = 0;
      
      for (const assessment of assessmentsList) {
        try {
          if (assessment.status === 'draft') {
            await axios.delete(`${API}/assessments/draft/${assessment.id}`, authHeaders(authToken));
          } else {
            await axios.delete(`${API}/assessments/${assessment.id}`, authHeaders(authToken));
          }
          deletedCount++;
        } catch (err) {
          console.error(`Failed to delete assessment ${assessment.id}:`, err);
          failedCount++;
        }
      }
      
      // Reload assessments after deletion
      loadAssessments();
      
      if (failedCount > 0) {
        alert(`Deleted ${deletedCount} assessment(s). ${failedCount} failed to delete.`);
      } else {
        alert(`All ${deletedCount} assessment(s) for "${clientName}" have been permanently deleted.`);
      }
    } catch (error) {
      console.error("Error deleting client assessments:", error);
      alert("Error deleting assessments. Please try again.");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreSummary = (assessment) => {
    const scores = assessment.scores || {};
    const type = assessment.assessment_type;
    
    // Handle different assessment types
    if (type === "DASS-21" || type === "DASS-42") {
      return `Depression: ${scores.depression_severity || 'N/A'}, Anxiety: ${scores.anxiety_severity || 'N/A'}, Stress: ${scores.stress_severity || 'N/A'}`;
    } else if (type === "ACE-Parent") {
      return `Inattention: ${scores.inattention_count || 0}/9, Hyperactive: ${scores.hyperactive_impulsive_count || 0}/9 - ${scores.presentation || 'N/A'}`;
    } else if (type === "MIGDAS-Teacher" || type === "DASI-Background" || type === "Neuro-Dev-Pre") {
      return `Completion: ${scores.completion_percentage || 0}%`;
    } else if (type === "DASI-IR" || type === "DASI-SR") {
      return `Total: ${scores.total_score || 0}/${scores.total_max || 120} - ${scores.overall_severity || 'N/A'}`;
    } else if (scores.total_score !== undefined) {
      return `Score: ${scores.total_score}${scores.total_max ? `/${scores.total_max}` : ''}`;
    } else if (scores.completion_percentage !== undefined) {
      return `Completion: ${scores.completion_percentage}%`;
    }
    return "View details";
  };

  if (loading && !loadedOnce) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
        <span className="ml-2 text-slate-600">Loading assessments...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {showPrepare && <PrepareAgreement authToken={authToken} onClose={() => setShowPrepare(false)} />}
      {showSignature && <ProviderSignatureManager authToken={authToken} onClose={() => setShowSignature(false)} />}
      {showCleanup && <DraftCleanupPanel authToken={authToken} onClose={() => setShowCleanup(false)} />}
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBack}
                className="text-slate-600 hover:text-slate-900"
                data-testid="admin-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Assessments
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="text-xl font-semibold text-slate-800">Admin Dashboard</h1>
            </div>
            <div className="text-sm text-slate-500 flex items-center gap-4">
              <span>
                {totalCompleted} completed • {totalDrafts} in progress • {totals.clients} clients
              </span>
              {retentionNote(retention) && (
                <span className="text-xs text-slate-500" data-testid="draft-retention-note">{retentionNote(retention)}</span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPrepare(true)}
                className="text-slate-700"
                data-testid="prepare-agreement-open-btn"
              >
                <FilePlus2 className="h-4 w-4 mr-2" />
                Prepare service agreement
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSignature(true)}
                className="text-slate-700"
                data-testid="provider-signature-open-btn"
              >
                <PenLine className="h-4 w-4 mr-2" />
                Provider signature
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCleanup(true)}
                className="text-slate-700"
                data-testid="draft-cleanup-open-btn"
              >
                <Clock className="h-4 w-4 mr-2" />
                Draft clean-up
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLogout}
                className="text-slate-600"
                data-testid="admin-logout-btn"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="admin-search-input"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Button 
              variant={statusFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter("all")}
              data-testid="filter-all-btn"
            >
              All
            </Button>
            <Button 
              variant={statusFilter === "completed" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter("completed")}
              data-testid="filter-completed-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Completed
            </Button>
            <Button 
              variant={statusFilter === "draft" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter("draft")}
              data-testid="filter-draft-btn"
            >
              <Clock className="h-4 w-4 mr-1" />
              In Progress
            </Button>
            <Button
              variant={statusFilter === "nopdf" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter("nopdf")}
              data-testid="filter-nopdf-btn"
              title="Finished assessments whose PDF was never made"
            >
              <FileText className="h-4 w-4 mr-1" />
              No PDF ({totalNoPdf})
            </Button>
          </div>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadAssessments}
            data-testid="admin-refresh-btn"
          >
            Refresh
          </Button>
        </div>

        {/* Client List */}
        {filteredClients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                {searchTerm ? "No clients found matching your search" : "No assessments submitted yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredClients.map(clientName => (
              <Card key={clientName} className="overflow-hidden" data-testid={`client-card-${clientName.replace(/\s+/g, '-')}`}>
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50 transition-colors py-4"
                  onClick={() => toggleClient(clientName)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expandedClients[clientName] ? (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      )}
                      <CardTitle className="text-lg font-medium text-slate-800">
                        {clientName}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      {groupedAssessments[clientName].some(a => a.status === 'draft') && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {groupedAssessments[clientName].filter(a => a.status === 'draft').length} in progress
                        </span>
                      )}
                      <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                        {groupedAssessments[clientName].filter(a => a.status !== 'draft').length} completed
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          regenerateAllPdfsForClient(clientName);
                        }}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 ml-2"
                        data-testid={`regenerate-all-${clientName.replace(/\s+/g, '-')}`}
                        title="Regenerate all PDFs for this client"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Regen All PDFs
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAllClientAssessments(clientName);
                        }}
                        className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                        data-testid={`delete-client-${clientName.replace(/\s+/g, '-')}`}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete All
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                {expandedClients[clientName] && (
                  <CardContent className="pt-0 pb-4">
                    <div className="space-y-3 ml-8">
                      {groupedAssessments[clientName].map((assessment, idx) => (
                        <div 
                          key={assessment.id || idx} 
                          className={`border rounded-lg overflow-hidden ${assessment.status === 'draft' ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`}
                          data-testid={`assessment-item-${assessment.id}`}
                        >
                          {/* Assessment Header */}
                          <div 
                            className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${assessment.status === 'draft' ? 'bg-amber-50 hover:bg-amber-100' : 'bg-slate-50 hover:bg-slate-100'}`}
                            onClick={() => toggleAssessment(assessment.id)}
                          >
                            <div className="flex items-center gap-3">
                              {expandedAssessments[assessment.id] ? (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-700">
                                    {assessment.metadata?.assessment_name || assessment.assessment_type}
                                  </span>
                                  {assessment.status === 'draft' ? (
                                    <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      In Progress ({assessment.progress_percent || 0}%)
                                    </span>
                                  ) : (
                                    <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" />
                                      Completed
                                    </span>
                                  )}
                                  {hasNoPdf(assessment) && (
                                    <span className="text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-testid={`no-pdf-${assessment.id}`}>
                                      No PDF
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-slate-500">
                                  {assessment.status === 'draft' ? (
                                    <>
                                      {assessment.answered_questions || 0}/{assessment.total_questions || '?'} questions • Last saved: {formatDate(assessment.last_saved || assessment.timestamp)}
                                      {draftRowNote(assessment, retention) && (
                                        <span data-testid={`draft-deletes-on-${assessment.id}`}> • {draftRowNote(assessment, retention)}</span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {formatDate(assessment.timestamp || assessment.date)} • {getScoreSummary(assessment)}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {assessment.status === 'draft' ? (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadDraftPdf(assessment.id);
                                    }}
                                    className="flex items-center gap-2"
                                    data-testid={`download-draft-pdf-${assessment.id}`}
                                  >
                                    <Download className="h-4 w-4" />
                                    PDF
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteDraft(assessment.id);
                                    }}
                                    className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-draft-${assessment.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadPdf(assessment.id);
                                    }}
                                    className="flex items-center gap-2"
                                    data-testid={`download-pdf-${assessment.id}`}
                                  >
                                    <Download className="h-4 w-4" />
                                    PDF
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      regeneratePdf(assessment.id, assessment.assessment_type);
                                    }}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    data-testid={`regenerate-pdf-${assessment.id}`}
                                    title={regenWarning(assessment.assessment_type) ? `Don't regenerate yet: ${regenWarning(assessment.assessment_type)}` : "Regenerate PDF if download fails"}
                                  >
                                    <RefreshCw className="h-4 w-4" />
                                    Regen
                                  </Button>
                                  {regenWarning(assessment.assessment_type) && (
                                    <span
                                      className="text-xs text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full"
                                      title={regenWarning(assessment.assessment_type)}
                                      data-testid={`regen-warning-${assessment.id}`}
                                    >
                                      Don't regenerate yet
                                    </span>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteAssessment(assessment.id, clientName);
                                    }}
                                    className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-assessment-${assessment.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Assessment Details */}
                          {expandedAssessments[assessment.id] && (
                            <div className="p-4 border-t border-slate-200 bg-white">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-slate-500">Assessment ID:</span>
                                  <span className="ml-2 font-mono text-slate-700">{assessment.id}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Date:</span>
                                  <span className="ml-2 text-slate-700">{assessment.date}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Type:</span>
                                  <span className="ml-2 text-slate-700">{assessment.assessment_type}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Questions:</span>
                                  <span className="ml-2 text-slate-700">{assessment.metadata?.num_questions || 'N/A'}</span>
                                </div>
                              </div>
                              
                              {/* Draft Responses - Show answers for in-progress assessments */}
                              {assessment.status === 'draft' && assessment.responses && (
                                <div className="mt-4">
                                  <h4 className="text-sm font-medium text-slate-700 mb-2">
                                    Responses ({assessment.answered_questions || Object.keys(assessment.responses).length} answered)
                                  </h4>
                                  <div className="bg-amber-50 rounded-lg p-3 max-h-96 overflow-y-auto">
                                    <div className="space-y-2">
                                      {Object.entries(assessment.responses)
                                        .filter(([key, value]) => value !== null && value !== '')
                                        .map(([questionId, answer]) => (
                                          <div key={questionId} className="flex text-sm border-b border-amber-100 pb-2 last:border-0">
                                            <span className="text-amber-700 font-medium min-w-[80px]">Q{questionId}:</span>
                                            <span className="text-slate-700 ml-2">
                                              {typeof answer === 'object' ? JSON.stringify(answer) : String(answer)}
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Scores - Only for completed assessments */}
                              {assessment.status !== 'draft' && assessment.scores && Object.keys(assessment.scores).length > 0 && (
                                <div className="mt-4">
                                  <h4 className="text-sm font-medium text-slate-700 mb-2">Scores</h4>
                                  <div className="bg-slate-50 rounded-lg p-3">
                                    <pre className="text-xs text-slate-600 overflow-x-auto">
                                      {JSON.stringify(assessment.scores, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                              
                              {/* Round 2: the saved written paragraph (clinical_interpretation) is not shown.
                                  It stays on the record unchanged; the PDF carries the scores and one plain line. */}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* One page at a time, newest first */}
        {matching > 0 && (
          <div className="mt-6 flex flex-col items-center gap-2" data-testid="admin-paging">
            <p className="text-sm text-slate-500" data-testid="admin-showing">
              {showingLine(assessments.length, matching)}
            </p>
            {hasMore && (
              <Button
                variant="outline"
                size="sm"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="admin-load-more-btn"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Load more
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
